import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';

export interface JobCode {
  "@id": string;
  "@type": string;
  "id": number;
  "title": string; // 2-digit padded number (e.g., "01", "08", "12")
  "description": string;
  "workplace"?: string; // Workplace IRI (e.g., "/api/workplaces/1")
}

export const getJobCodes = async (workplaceId?: number): Promise<JobCode[]> => {
  const id = workplaceId || Number(localStorage.getItem("workplace-id"));
  // Build query parameters
  const params = new URLSearchParams();
  params.append('pagination', 'false');
  if (id && !Number.isNaN(id)) {
    params.append('workplace', id.toString());
  }
  
  const data = await paginationService.getWithHandler(`guest_connect_rewards?${params.toString()}`);
  return data?.member || data?.['hydra:member'] || [];
};

export const createJobCode = async (jobCode: Partial<JobCode>): Promise<JobCode> => {
  const { data } = apiService.responseHandler(
    await apiService.post('guest_connect_rewards', jobCode)
  );
  return data;
};

export const updateJobCode = async (id: number, jobCode: Partial<JobCode>): Promise<JobCode> => {
  const { data } = apiService.responseHandler(
    await apiService.put(`guest_connect_rewards/${id}`, jobCode)
  );
  return data;
};

export const deleteJobCode = async (id: number): Promise<void> => {
  await apiService.delete(`guest_connect_rewards/${id}`);
};

// General Settings API
export interface GeneralSettings {
  "@id"?: string;
  "@type"?: string;
  "id"?: number;
  "allowMultipleSubmissionsPerDay": boolean | null;
  "disclaimerText": string;
  "logoPath": string;
  "workplace": string;
}

export const getGeneralSettings = async (workplaceId?: number): Promise<GeneralSettings | null> => {
  try {
    const id = workplaceId || Number(localStorage.getItem("workplace-id"));
    // Build query parameters
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    if (id && !Number.isNaN(id)) {
      params.append('workplace', id.toString());
    }
    
    const data = await paginationService.getWithHandler(`guest_connect_settings?${params.toString()}`);
    
    // Handle response structure - could be { member: [...] } or direct array
    let items: GeneralSettings[] = [];
    if (Array.isArray(data)) {
      items = data;
    } else if (Array.isArray(data?.member)) {
      items = data.member;
    } else if (Array.isArray(data?.['hydra:member'])) {
      items = data['hydra:member'];
    }
    
    return items.length > 0 ? items[0] : null;
  } catch (error) {
    console.error('Error fetching general settings:', error);
    return null;
  }
};

export const createGeneralSettings = async (settings: any): Promise<GeneralSettings> => {
  // If logoFile is a File object, use FormData, otherwise use JSON
  if (settings.logoFile instanceof File) {
    const formData = new FormData();
    formData.append('workplace', settings.workplace);
    formData.append('disclaimerText', settings.disclaimerText || '');
    formData.append('allowMultipleSubmissionsPerDay', String(settings.allowMultipleSubmissionsPerDay || false));
    // Append file with name - this is important for server to recognize it
    formData.append('logoFile', settings.logoFile, settings.logoFile.name);
    
    // Debug: Log FormData contents to verify file is being sent
    console.log('=== FormData Debug ===');
    console.log('workplace:', settings.workplace);
    console.log('disclaimerText:', settings.disclaimerText);
    console.log('allowMultipleSubmissionsPerDay:', settings.allowMultipleSubmissionsPerDay);
    console.log('logoFile name:', settings.logoFile.name);
    console.log('logoFile size:', settings.logoFile.size, 'bytes');
    console.log('logoFile type:', settings.logoFile.type);
    console.log('logoFile lastModified:', new Date(settings.logoFile.lastModified));
    
    // Use apiService.postUpload for FormData uploads
    const { data } = apiService.responseHandler(
      await apiService.postUpload('guest-connect-setting', formData)
    );
    return data;
  } else {
    // For JSON requests (when no file or logoFile is null)
    const payload: any = {
      workplace: settings.workplace,
      disclaimerText: settings.disclaimerText,
      allowMultipleSubmissionsPerDay: settings.allowMultipleSubmissionsPerDay,
      logoPath: settings.logoPath !== undefined ? settings.logoPath : null,
      logoFile: settings.logoFile !== undefined ? settings.logoFile : null,
    };
    
    // Use /json endpoint for JSON requests
    const { data } = apiService.responseHandler(
      await apiService.post('guest-connect-setting/json', payload)
    );
    return data;
  }
};

export const updateGeneralSettings = async (id: number, settings: any): Promise<GeneralSettings> => {
  // Use the same endpoint as create - API handles create/update based on existing record
  // If logoFile is a File object, use FormData, otherwise use JSON
  if (settings.logoFile instanceof File) {
    const formData = new FormData();
    formData.append('workplace', settings.workplace);
    formData.append('disclaimerText', settings.disclaimerText || '');
    formData.append('allowMultipleSubmissionsPerDay', String(settings.allowMultipleSubmissionsPerDay || false));
    // Append file with name - this is important for server to recognize it
    formData.append('logoFile', settings.logoFile, settings.logoFile.name);
    
    // Debug: Log FormData contents to verify file is being sent
    console.log('=== Update FormData Debug ===');
    console.log('ID:', id);
    console.log('workplace:', settings.workplace);
    console.log('disclaimerText:', settings.disclaimerText);
    console.log('allowMultipleSubmissionsPerDay:', settings.allowMultipleSubmissionsPerDay);
    console.log('logoFile name:', settings.logoFile.name);
    console.log('logoFile size:', settings.logoFile.size, 'bytes');
    console.log('logoFile type:', settings.logoFile.type);
    
    // Use apiService.postUpload for FormData uploads
    const { data } = apiService.responseHandler(
      await apiService.postUpload('guest-connect-setting', formData)
    );
    return data;
  } else {
    // For JSON requests (when no file or logoFile is null)
    const payload: any = {
      workplace: settings.workplace,
      disclaimerText: settings.disclaimerText,
      allowMultipleSubmissionsPerDay: settings.allowMultipleSubmissionsPerDay,
      logoPath: settings.logoPath !== undefined ? settings.logoPath : null,
      logoFile: settings.logoFile !== undefined ? settings.logoFile : null,
    };
    
    // Use /json endpoint for JSON requests
    const { data } = apiService.responseHandler(
      await apiService.post('guest-connect-setting/json', payload)
    );
    return data;
  }
};
